import { Search, X } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { Button, Checkbox, Input } from "~/components/ui";
import { useEditMapTools } from "~/features/recordGeoItems/contexts/EditMapToolsContext";
import { useComparisonAreaAdd } from "~/features/recordGeoItems/hooks/useComparisonAreaAdd";
import { useLocationSearch } from "~/features/recordGeoItems/hooks/useLocationSearch";
import { geoItemDisplayName } from "~/features/recordGeoItems/utils/geoItemLabel";
import type { ComparisonArea, CoordsType, Location } from "~/lib/map/types";

interface EditMapToolsProps {
  /** Pan the map to a coordinate (wired to the EditMap `moveTo` handle). */
  onMoveTo: (coords: CoordsType) => void;
}

/** Docked editor tools: location search, in-viewport discovery, pinned locations, comparison areas. */
export function EditMapTools({ onMoveTo }: EditMapToolsProps) {
  const { t } = useTranslation();
  const {
    isActiveInViewportLocations,
    setIsActiveInViewportLocations,
    setSearchedLocation,
    pinnedLocations,
    setPinnedLocations,
    comparisonAreas,
    setComparisonAreas,
  } = useEditMapTools();

  return (
    <div className="space-y-4 p-4 text-[13px]">
      <LocationSearchForm onFound={(loc) => setSearchedLocation(loc)} />

      <label htmlFor="in-viewport-locations" className="flex items-center gap-2 text-fg-1">
        <Checkbox
          id="in-viewport-locations"
          checked={isActiveInViewportLocations}
          onCheckedChange={(c) => setIsActiveInViewportLocations(c === true)}
        />
        <span>{t("feature.recordGeoItems.inViewportLocations.switchLabel")}</span>
      </label>

      <Section title={t("feature.recordGeoItems.pinnedLocations.title")}>
        {pinnedLocations.length === 0 ? (
          <p className="text-fg-3">{t("feature.common.noData")}</p>
        ) : (
          pinnedLocations.map((loc, i) => (
            <Row
              key={loc.geoItemId}
              checked={loc.isShow}
              onCheckedChange={(show) =>
                setPinnedLocations(pinnedLocations.map((l, idx) => (idx === i ? { ...l, isShow: show } : l)))
              }
              label={geoItemDisplayName(loc.geoItemId, loc.label)}
              onLabelClick={() => onMoveTo(loc.coords)}
              onRemove={() => setPinnedLocations(pinnedLocations.filter((_, idx) => idx !== i))}
            />
          ))
        )}
      </Section>

      <Section title={t("feature.recordGeoItems.comparisonAreas.title")}>
        <ComparisonAreaAddForm
          onAdded={(area) => {
            if (!area) return;
            if (comparisonAreas.some((a) => a.geoItemId === area.geoItemId)) {
              toast.info(t("feature.recordGeoItems.comparisonAreas.alert.alreadyRegistered"));
              return;
            }
            setComparisonAreas([...comparisonAreas, area]);
          }}
        />
        {comparisonAreas.map((area, i) => (
          <Row
            key={area.geoItemId}
            checked={area.isShow}
            onCheckedChange={(show) =>
              setComparisonAreas(comparisonAreas.map((a, idx) => (idx === i ? { ...a, isShow: show } : a)))
            }
            label={area.label}
            onLabelClick={() => {
              if (area.coords) {
                onMoveTo(area.coords);
              }
            }}
            onRemove={() => setComparisonAreas(comparisonAreas.filter((_, idx) => idx !== i))}
          />
        ))}
      </Section>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-2 border-t border-border-default pt-3">
      <h3 className="text-12-bold capitalize text-fg-2">{title}</h3>
      {children}
    </div>
  );
}

function Row({
  checked,
  onCheckedChange,
  label,
  onLabelClick,
  onRemove,
}: {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  label: string;
  onLabelClick: () => void;
  onRemove: () => void;
}) {
  return (
    <div className="flex items-center gap-2">
      <Checkbox checked={checked} onCheckedChange={(c) => onCheckedChange(c === true)} />
      <button
        type="button"
        onClick={onLabelClick}
        className="flex-1 whitespace-pre-line text-left text-fg-link hover:underline"
      >
        {label}
      </button>
      <Button type="button" variant="ghost" size="icon" className="h-6 w-6" aria-label={label} onClick={onRemove}>
        <X className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
}

function LocationSearchForm({ onFound }: { onFound: (location: Location) => void }) {
  const { t } = useTranslation();
  const [text, setText] = useState("");
  const { searchLocation, processing } = useLocationSearch(onFound);

  return (
    <div className="flex items-center gap-2">
      <Input
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={t("feature.recordGeoItems.locationSearch.placeholder")}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            void searchLocation(text);
          }
        }}
      />
      <Button
        type="button"
        variant="outline"
        size="icon"
        aria-label={t("ui.search")}
        disabled={processing}
        onClick={() => void searchLocation(text)}
      >
        <Search className="h-4 w-4" />
      </Button>
    </div>
  );
}

function ComparisonAreaAddForm({ onAdded }: { onAdded: (area: ComparisonArea | null) => void }) {
  const { t } = useTranslation();
  const [geoItemId, setGeoItemId] = useState("");
  const { submit, processing } = useComparisonAreaAdd((area) => {
    onAdded(area);
    if (area) setGeoItemId("");
  });

  return (
    <div className="flex items-center gap-2">
      <Input
        value={geoItemId}
        onChange={(e) => setGeoItemId(e.target.value)}
        placeholder={t("feature.recordGeoItems.comparisonAreas.id")}
      />
      <Button type="button" variant="outline" size="sm" disabled={processing} onClick={() => void submit(geoItemId)}>
        {t("ui.add")}
      </Button>
    </div>
  );
}
